import { action, makeObservable, observable } from 'mobx';
import { apiAuthCaller } from '@services/apiCaller';
import { IRootStore } from '@stores/index';
import { AlertDefinition, ResourceCategory, ResourceType } from './types';

// P95 latency (id 8) is a plan feature; High event volume (id 12) is behind its rollout flag
const P95_LATENCY_ALERT_DEFINITION_ID = '8';
const HIGH_EVENT_VOLUME_ALERT_DEFINITION_ID = '12';

export interface IAlertDefinitionsListStore {
  alertDefinitions: AlertDefinition[];
  isHighEventVolumeAlertEnabled: boolean;
  load(): Promise<void>;
  setHighEventVolumeAlertEnabled(enabled: boolean): void;
  getAlertDefinitionsByResourceCategory(
    resourceCategory: ResourceCategory,
    resourceType?: ResourceType,
  ): AlertDefinition[];
}

export class AlertDefinitionsListStore implements IAlertDefinitionsListStore {
  public rootStore: IRootStore;

  public alertDefinitions: AlertDefinition[] = [];

  // Rollout flag `enable-high-event-volume-alert`, injected by the alerts UI (see useHighEventVolumeAlertFlag).
  @observable public isHighEventVolumeAlertEnabled = false;

  @action.bound
  public setHighEventVolumeAlertEnabled(enabled: boolean) {
    this.isHighEventVolumeAlertEnabled = enabled;
  }

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
  }

  @action.bound
  public async load() {
    if (this.alertDefinitions.length > 0) {
      return;
    }
    const res = await apiAuthCaller().get<AlertDefinition[]>(
      '/alertNotifications/alertDefinitions',
    );
    this.alertDefinitions = res.data;
  }

  public getAlertDefinitionsByResourceCategory = (
    resourceCategory: ResourceCategory,
    resourceType?: ResourceType,
  ) => {
    const hiddenDefinitionIds = new Set<string>();
    if (!this.rootStore.featuresStore.has('P95_LATENCY_ALERT')) {
      hiddenDefinitionIds.add(P95_LATENCY_ALERT_DEFINITION_ID);
    }
    if (!this.isHighEventVolumeAlertEnabled) {
      hiddenDefinitionIds.add(HIGH_EVENT_VOLUME_ALERT_DEFINITION_ID);
    }

    return this.alertDefinitions.filter(
      (def) =>
        !hiddenDefinitionIds.has(String(def.id)) &&
        def.resourceCategory === resourceCategory &&
        (!resourceType || def.resourceType === resourceType),
    );
  };
}
